import { computed, reactive, readonly, ref } from 'vue';

interface LayoutConfig {
  preset: string;
  primary: string | null;
  surface: string | null;
  darkTheme: boolean;
  menuMode: 'static' | 'overlay';
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
  activeMenuItem: string | null;
}

// Get the initial theme from localStorage or default to true (dark mode)
const getInitialTheme = (): boolean => {
  const savedTheme = localStorage.getItem('darkTheme');
  return savedTheme !== null ? JSON.parse(savedTheme) : true;
};

const layoutConfig = reactive<LayoutConfig>({
  preset: 'Aura',
  primary: 'red',
  surface: null,
  darkTheme: getInitialTheme(),
  menuMode: 'static',
});

const layoutState = reactive<LayoutState>({
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false,
  activeMenuItem: null,
});

export function useLayout() {
  const setPrimary = (value: string) => {
    layoutConfig.primary = value;
  };

  const setSurface = (value: string) => {
    layoutConfig.surface = value;
  };

  const setPreset = (value: string) => {
    layoutConfig.preset = value;
  };

  const setActiveMenuItem = (item: { value?: string } | string) => {
    layoutState.activeMenuItem = typeof item === 'string' ? item : item.value || null;
  };

  const setMenuMode = (mode: 'static' | 'overlay') => {
    layoutConfig.menuMode = mode;
  };

  // Save theme to localStorage
  const saveTheme = (isDark: boolean) => {
    localStorage.setItem('darkTheme', JSON.stringify(isDark));
  };

  const toggleDarkMode = () => {
    if (!document.startViewTransition) {
      if (layoutConfig.darkTheme) {
        executeLightModeToggle();
      } else {
        executeDarkModeToggle();
      }
    } else {
      if (layoutConfig.darkTheme) {
        document.startViewTransition(() => executeLightModeToggle());
      } else {
        document.startViewTransition(() => executeDarkModeToggle());
      }
    }
  };

  const executeLightModeToggle = () => {
    layoutConfig.darkTheme = false;
    document.documentElement.classList.toggle('app-dark', false);
    saveTheme(false);
  };

  const executeDarkModeToggle = () => {
    layoutConfig.darkTheme = true;
    document.documentElement.classList.toggle('app-dark', true);
    saveTheme(true);
  };

  // Initialize theme
  const initializeTheme = () => {
    const isDark = getInitialTheme();
    if (isDark) {
      executeDarkModeToggle();
    } else {
      executeLightModeToggle();
    }
  };

  const onMenuToggle = () => {
    if (layoutConfig.menuMode === 'overlay') {
      layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
    }

    if (window.innerWidth > 991) {
      layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
    } else {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
    }
  };

  const resetMenu = () => {
    layoutState.overlayMenuActive = false;
    layoutState.staticMenuMobileActive = false;
    layoutState.menuHoverActive = false;
  };

  const isSidebarActive = computed(
    () => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
  );

  const isDarkTheme = computed(() => layoutConfig.darkTheme);

  const getPrimary = computed(() => layoutConfig.primary);

  const getSurface = computed(() => layoutConfig.surface);

  return {
    layoutConfig: readonly(layoutConfig),
    layoutState: readonly(layoutState),
    onMenuToggle,
    isSidebarActive,
    isDarkTheme,
    getPrimary,
    getSurface,
    setActiveMenuItem,
    toggleDarkMode,
    executeLightModeToggle,
    executeDarkModeToggle,
    setPrimary,
    setSurface,
    setPreset,
    resetMenu,
    setMenuMode,
    initializeTheme,
  };
}
